import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Clock, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { timeclockApi } from "../lib/api";
import { getCurrentPositionOnce } from "../lib/gps";

/**
 * Global "would you like to clock in?" popup that appears once per session
 * right after a successful login. Two options: "Clock in now" or "Not at
 * this time". After the user makes a choice we simply close the modal —
 * they were already routed to their home screen by the login flow.
 *
 * Skips (silently) when:
 *   • the user is super_admin (platform console — see Login.destForRole)
 *   • the current route lives under /platform, /login, /register, /auth,
 *     /pay/, /accept/, /f/ (public / auth pages)
 *   • the company plan doesn't include the timeclock module
 *   • the user is already clocked_in or on_lunch (would be annoying)
 *
 * The trigger is a session-storage flag `clockin_prompt_pending` set by
 * AuthContext on `login()` / `register()` and by AuthCallback after SSO.
 */
export default function ClockInPromptHost() {
  const { user, hasModule } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const dismiss = useCallback(() => {
    sessionStorage.removeItem("clockin_prompt_pending");
    setOpen(false);
  }, []);

  useEffect(() => {
    // Public / auth pages: never show and don't touch the flag — the user
    // will land on their real home screen a moment later.
    const path = location.pathname || "";
    const isPublic =
      path.startsWith("/platform") ||
      path.startsWith("/login") ||
      path.startsWith("/register") ||
      path.startsWith("/auth") ||
      path.startsWith("/forgot-password") ||
      path.startsWith("/reset-password") ||
      path.startsWith("/pay/") ||
      path.startsWith("/accept/") ||
      path.startsWith("/f/");
    if (isPublic) return;

    // Wait until AuthContext has hydrated a real user object (null = probing).
    if (!user || user === false) return;
    // Platform admins never see the prompt.
    if (user.role === "super_admin") {
      sessionStorage.removeItem("clockin_prompt_pending");
      return;
    }
    // Only show when the login flow explicitly flagged us.
    if (sessionStorage.getItem("clockin_prompt_pending") !== "1") return;
    // Respect the module gate — no point asking if the company doesn't own it.
    if (!hasModule("timeclock")) {
      sessionStorage.removeItem("clockin_prompt_pending");
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const me = await timeclockApi.me();
        if (cancelled) return;
        // If they're mid-shift already (returning after a browser refresh) we
        // don't need to nag. Consume the flag either way so we don't ask again.
        if (me?.status === "clocked_in" || me?.status === "on_lunch") {
          sessionStorage.removeItem("clockin_prompt_pending");
          return;
        }
        setOpen(true);
      } catch (_) {
        // /timeclock/me can 403 for accounts without a linked technician
        // profile. Fail closed — just skip the prompt so nothing looks broken.
        sessionStorage.removeItem("clockin_prompt_pending");
      }
    })();

    return () => { cancelled = true; };
  }, [user, hasModule, location.pathname]);

  const clockIn = async () => {
    setBusy(true);
    try {
      const gps = await getCurrentPositionOnce();
      await timeclockApi.clockIn(gps);
      toast.success("Clocked in — have a great shift!");
      window.dispatchEvent(new Event("timeclock-changed"));
      dismiss();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not clock in — try again from your profile menu.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="clockin-prompt-title"
      data-testid="clockin-prompt"
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      style={{ backgroundColor: "rgba(15, 23, 42, 0.55)" }}
    >
      <div
        className="w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200"
        style={{ backgroundColor: "#ffffff", border: "1px solid #e2e8f0" }}
      >
        <div className="flex items-start justify-between px-5 pt-5 pb-2">
          <div
            className="h-11 w-11 rounded-full flex items-center justify-center shrink-0"
            style={{ backgroundColor: "#ecfdf5", border: "1px solid #a7f3d0" }}
          >
            <Clock className="h-5 w-5" style={{ color: "#059669" }} strokeWidth={2.5} />
          </div>
          <button
            type="button"
            onClick={dismiss}
            disabled={busy}
            data-testid="clockin-prompt-close"
            title="Close"
            className="h-8 w-8 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 disabled:opacity-40"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 pb-4">
          <h2
            id="clockin-prompt-title"
            className="text-[18px] font-bold leading-tight"
            style={{ color: "#0f172a" }}
          >
            Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}!
          </h2>
          <p className="mt-1.5 text-[13.5px] leading-snug" style={{ color: "#475569" }}>
            Would you like to clock in for your shift now? You can always start it later from your profile menu.
          </p>
        </div>

        <div className="px-5 pb-5 pt-1 flex flex-col gap-2">
          <button
            type="button"
            onClick={clockIn}
            disabled={busy}
            data-testid="clockin-prompt-yes"
            className="h-11 rounded-lg font-semibold text-[14px] flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
            style={{ backgroundColor: "#059669", color: "#ffffff" }}
            onMouseEnter={(e) => { if (!busy) e.currentTarget.style.backgroundColor = "#047857"; }}
            onMouseLeave={(e) => { if (!busy) e.currentTarget.style.backgroundColor = "#059669"; }}
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" style={{ color: "#ffffff" }} />
            ) : (
              <Clock className="h-4 w-4" style={{ color: "#ffffff" }} strokeWidth={2.5} />
            )}
            Clock in now
          </button>
          <button
            type="button"
            onClick={dismiss}
            disabled={busy}
            data-testid="clockin-prompt-no"
            className="h-11 rounded-lg font-semibold text-[13.5px] transition-colors disabled:opacity-60"
            style={{ backgroundColor: "#f1f5f9", color: "#334155", border: "1px solid #cbd5e1" }}
            onMouseEnter={(e) => { if (!busy) e.currentTarget.style.backgroundColor = "#e2e8f0"; }}
            onMouseLeave={(e) => { if (!busy) e.currentTarget.style.backgroundColor = "#f1f5f9"; }}
          >
            Not at this time
          </button>
        </div>
      </div>
    </div>
  );
}
